import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  Search, Filter, Calendar, CheckCircle2, XCircle, Eye, 
  LayoutGrid, List, ChevronDown, X, Clock, FileText,
  User, Mail, Phone, MessageSquare, RefreshCw, Download, Video,
  Archive, ArchiveRestore, Sparkles, Zap, ShieldCheck, Briefcase, Repeat
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { companyApi } from '../../../lib/api';
import RequestDetailModal from '../../../components/RequestDetailModal';

// Configuration des types de demandes
const REQUEST_TYPES = {
  subscription: { label: 'Abonnement', color: 'bg-purple-100 text-purple-800', prefix: 'ABO' },
  punctual: { label: 'Ponctuelle', color: 'bg-blue-100 text-blue-800', prefix: 'RES' },
  appointment: { label: 'RDV', color: 'bg-green-100 text-green-800', prefix: 'RDV' },
  quote: { label: 'Devis', color: 'bg-amber-100 text-amber-800', prefix: 'DEV' },
};

// Configuration des statuts
const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', order: 1 },
  // Unify "confirmed" + "approved" → display as "Approuvé"
  confirmed: { label: 'Approuvé', color: 'bg-green-100 text-green-800', order: 2 },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800', order: 2 },
  advice_requested: { label: 'Demande conseil', color: 'bg-purple-100 text-purple-800', order: 1 },
  awaiting_dates: { label: 'Attente dates', color: 'bg-orange-100 text-orange-800', order: 1 },
  awaiting_client_validation: { label: 'Valid. client', color: 'bg-indigo-100 text-indigo-800', order: 1 },
  completed: { label: 'Terminé', color: 'bg-blue-100 text-blue-800', order: 3 },
  // Unify "cancelled" + "rejected" → display as "Refusé"
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-800', order: 4 },
  cancelled: { label: 'Refusé', color: 'bg-red-100 text-red-800', order: 4 },
};

// Pill filters for the new powerful UX
const TYPE_PILLS = [
  { value: 'all', label: 'Toutes', icon: Sparkles, color: 'bg-gray-900 text-white' },
  { value: 'appointment-physical', label: 'RDV physique', icon: User, color: 'bg-blue-600 text-white', filter: r => r.request_type === 'appointment' && r.meeting_type !== 'online' && r.meeting_type !== 'video' },
  { value: 'appointment-video', label: 'RDV visio', icon: Video, color: 'bg-indigo-600 text-white', filter: r => r.request_type === 'appointment' && (r.meeting_type === 'online' || r.meeting_type === 'video') },
  { value: 'punctual', label: 'Ponctuelles', icon: Briefcase, color: 'bg-emerald-600 text-white' },
  { value: 'subscription', label: 'Abonnements', icon: Repeat, color: 'bg-purple-600 text-white' },
];

// Tabs for the bucketed view
const TABS = [
  { value: 'todo', label: 'À traiter', icon: Clock, color: 'text-orange-700 bg-orange-50 border-orange-200' },
  { value: 'auto', label: 'Validées auto', icon: Zap, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  { value: 'archived', label: 'Archivées', icon: Archive, color: 'text-gray-700 bg-gray-100 border-gray-200' },
];

// Colonnes Kanban
const KANBAN_COLUMNS = [
  { id: 'pending', title: 'En attente', statuses: ['pending', 'advice_requested', 'awaiting_dates', 'awaiting_client_validation'] },
  { id: 'approved', title: 'Approuvé', statuses: ['approved', 'confirmed'] },
  { id: 'completed', title: 'Terminé', statuses: ['completed'] },
  { id: 'rejected', title: 'Refusé', statuses: ['rejected', 'cancelled'] },
];

// Filtres de date prédéfinis
const DATE_FILTERS = [
  { value: 'all', label: 'Toutes les dates' },
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: 'Cette semaine' },
  { value: 'month', label: 'Ce mois' },
  { value: 'custom', label: 'Personnalisé...' },
];

export function UnifiedRequestsPage({ defaultTypeFilter = 'all', videoOnly = false, pageTitle, pageSubtitle } = {}) {
  // États
  const [allRequests, setAllRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'kanban'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Nouveau modèle de filtres : Pills + Tabs
  const [activePill, setActivePill] = useState(videoOnly ? 'appointment-video' : 'all');
  const [activeTab, setActiveTab] = useState('todo'); // todo | auto | archived

  // Filtres avancés
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(defaultTypeFilter);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  // Charger toutes les demandes via le nouvel endpoint enrichi
  const fetchAllRequests = async () => {
    setIsLoading(true);
    try {
      const res = await companyApi.getAllRequests();
      setAllRequests(res.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setIsLoading(false);
    }
  };

  // Action Archiver
  const handleArchive = async (request, archive = true) => {
    try {
      await companyApi.archiveRequest(request.id, archive);
      toast.success(archive ? 'Demande archivée' : 'Demande désarchivée');
      fetchAllRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  useEffect(() => {
    fetchAllRequests();
  }, []);

  // Filtrer les demandes
  const filteredRequests = useMemo(() => {
    return allRequests.filter(request => {
      // Filtre par recherche (numéro, nom, email)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesNumber = request.request_number?.toLowerCase().includes(query) || 
                             request.id?.toLowerCase().includes(query);
        const matchesName = request.client_name?.toLowerCase().includes(query);
        const matchesEmail = request.client_email?.toLowerCase().includes(query);
        if (!matchesNumber && !matchesName && !matchesEmail) return false;
      }

      // Pill filter (type-based)
      const pill = TYPE_PILLS.find(p => p.value === activePill);
      if (pill) {
        if (pill.filter) {
          if (!pill.filter(request)) return false;
        } else if (pill.value !== 'all') {
          if (request.request_type !== pill.value) return false;
        }
      }

      // Tab filter
      const isAuto = request.validation_mode === 'auto';
      const isClosed = ['completed', 'rejected', 'cancelled'].includes(request.status);
      const isArchived = !!request.is_archived;
      if (activeTab === 'archived') {
        if (!isArchived) return false;
      } else if (activeTab === 'auto') {
        if (isArchived) return false;
        // Auto-validated: status not pending (was auto-confirmed) AND not waiting for action
        if (!isAuto && !isClosed) return false;
        if (isClosed && !isAuto) return false; // closed + manual goes to "todo" by default? no, we hide closed from todo
      } else {
        // 'todo'
        if (isArchived) return false;
        if (isClosed) return false; // closed (completed/rejected/cancelled) → not "to do"
        if (isAuto && request.status === 'confirmed') return false; // auto-confirmed → bucket "auto"
      }

      // Filtre par statut (unifié : approved/confirmed et rejected/cancelled groupés)
      if (statusFilter !== 'all') {
        if (statusFilter === 'approved') {
          if (!['approved', 'confirmed'].includes(request.status)) return false;
        } else if (statusFilter === 'rejected') {
          if (!['rejected', 'cancelled'].includes(request.status)) return false;
        } else if (request.status !== statusFilter) {
          return false;
        }
      }

      // Filtre par date
      if (dateFilter !== 'all') {
        const requestDate = new Date(request.created_at);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dateFilter === 'today') {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          if (requestDate < today || requestDate >= tomorrow) return false;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          if (requestDate < weekAgo) return false;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          if (requestDate < monthAgo) return false;
        } else if (dateFilter === 'custom' && customDateStart && customDateEnd) {
          const start = new Date(customDateStart);
          const end = new Date(customDateEnd);
          end.setHours(23, 59, 59, 999);
          if (requestDate < start || requestDate > end) return false;
        }
      }

      return true;
    });
  }, [allRequests, searchQuery, activePill, activeTab, statusFilter, dateFilter, customDateStart, customDateEnd]);

  // Grouper par colonne Kanban
  const kanbanData = useMemo(() => {
    const data = {};
    KANBAN_COLUMNS.forEach(col => {
      data[col.id] = filteredRequests.filter(r => col.statuses.includes(r.status));
    });
    return data;
  }, [filteredRequests]);

  // Statistiques rapides
  const stats = useMemo(() => ({
    total: filteredRequests.length,
    pending: filteredRequests.filter(r => ['pending', 'advice_requested', 'awaiting_dates'].includes(r.status)).length,
    approved: filteredRequests.filter(r => ['approved', 'confirmed'].includes(r.status)).length,
    completed: filteredRequests.filter(r => r.status === 'completed').length,
  }), [filteredRequests]);

  // Handlers
  const handleApprove = async (request) => {
    try {
      if (request.request_type === 'subscription') {
        await companyApi.approveSubscription(request.id);
      } else if (request.request_type === 'appointment') {
        await companyApi.approveAppointment(request.id);
      } else if (request.request_type === 'punctual') {
        await companyApi.confirmBooking(request.id);
      }
      toast.success('Demande approuvée !');
      fetchAllRequests();
    } catch (error) {
      // Surface the real server error so the admin can act on it. Falling
      // back to the generic French message used to hide useful info like
      // "client account limit reached" or model validation errors.
      const detail = error?.response?.data?.detail;
      console.error('Approve error:', error);
      let msg = "Erreur lors de l'approbation";
      if (typeof detail === 'string' && detail) {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail.map(e => e.msg).join(' • ');
      } else if (detail?.message) {
        msg = detail.message;
      } else if (error?.response?.status >= 500) {
        msg = "Erreur serveur — vérifiez le détail de la demande puis réessayez.";
      }
      toast.error(msg);
    }
  };

  const handleReject = async (request) => {
    try {
      if (request.request_type === 'subscription') {
        await companyApi.rejectSubscription(request.id, 'Demande refusée');
      } else if (request.request_type === 'appointment') {
        await companyApi.rejectAppointment(request.id, 'Demande refusée');
      } else if (request.request_type === 'punctual') {
        await companyApi.cancelBooking(request.id);
      }
      toast.success('Demande refusée');
      fetchAllRequests();
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setIsDetailOpen(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit',
        year: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Reset filtres
  const resetFilters = () => {
    setSearchQuery('');
    setTypeFilter(defaultTypeFilter);
    setStatusFilter('all');
    setDateFilter('all');
    setCustomDateStart('');
    setCustomDateEnd('');
  };

  const hasActiveFilters = searchQuery || typeFilter !== defaultTypeFilter || statusFilter !== 'all' || dateFilter !== 'all';

  // Check if request has custom answers
  const hasCustomAnswers = (req) => {
    return req.custom_answers && Object.keys(req.custom_answers).length > 0;
  };

  // Render request card for Kanban
  const RequestCard = ({ request }) => {
    const typeConfig = REQUEST_TYPES[request.request_type] || {};
    const statusConfig = STATUS_CONFIG[request.status] || { label: request.status, color: 'bg-gray-100' };
    
    return (
      <div 
        className="bg-white rounded-lg border border-gray-200 p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDetail(request)}
      >
        <div className="flex items-start justify-between mb-2">
          <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            {request.request_number || `#${request.id?.substring(0, 6)}`}
          </span>
          <Badge className={`${typeConfig.color} text-xs`}>{typeConfig.label}</Badge>
        </div>
        <p className="font-medium text-sm truncate">{request.client_name}</p>
        <p className="text-xs text-muted-foreground truncate">{request.client_email}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">{formatDate(request.created_at)}</span>
          {hasCustomAnswers(request) && (
            <MessageSquare className="w-3 h-3 text-blue-500" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold">{pageTitle || 'Demandes'}</h1>
          <p className="text-muted-foreground mt-1">{pageSubtitle || 'Gérez toutes vos demandes en un seul endroit'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAllRequests}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Actualiser
          </Button>
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'table' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setViewMode('table')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-700">En attente</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-700">Approuvées</p>
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700">Terminées</p>
            <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bannière info pour les RDV en visio */}
      {videoOnly && (() => {
        const needLink = filteredRequests.filter(r => !r.meeting_link && r.status !== 'cancelled' && r.status !== 'completed').length;
        return (
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-center gap-4" data-testid="video-rdv-banner">
            <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700">
              <Video className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900">
                {needLink > 0
                  ? `${needLink} RDV en visio sans lien Google Meet`
                  : 'Tous vos RDV en visio ont leur lien'}
              </p>
              <p className="text-xs text-purple-700">
                Cliquez sur un RDV pour ajouter ou modifier son lien de visioconférence et l'envoyer au client.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Pills filtres par type — fluide et visuel */}
      <div className="flex flex-wrap gap-2" data-testid="type-pills">
        {TYPE_PILLS.map((pill) => {
          const isActive = activePill === pill.value;
          // Compute count for this pill
          const count = allRequests.filter(r => {
            if (pill.filter) return pill.filter(r);
            if (pill.value === 'all') return true;
            return r.request_type === pill.value;
          }).filter(r => !r.is_archived).length;
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => setActivePill(pill.value)}
              data-testid={`pill-${pill.value}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                isActive
                  ? `${pill.color} border-transparent shadow-md`
                  : 'bg-white text-foreground border-border hover:border-foreground/20 hover:bg-muted/40'
              }`}
            >
              <pill.icon className="w-4 h-4" />
              <span>{pill.label}</span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                isActive ? 'bg-white/20' : 'bg-muted text-muted-foreground'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tabs : À traiter / Validées auto / Archivées */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-1" data-testid="status-tabs">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.value;
          // Compute counts based on bucket logic
          const tabCount = allRequests.filter(r => {
            const isAuto = r.validation_mode === 'auto';
            const isClosed = ['completed', 'rejected', 'cancelled'].includes(r.status);
            const isArchived = !!r.is_archived;
            if (tab.value === 'archived') return isArchived;
            if (tab.value === 'auto') return !isArchived && (isAuto || isClosed);
            return !isArchived && !isClosed && !(isAuto && r.status === 'confirmed');
          }).length;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              data-testid={`tab-${tab.value}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors -mb-[1px] ${
                isActive
                  ? `${tab.color} border-b-2 border-current`
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 border-b-2 border-transparent'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                isActive ? 'bg-white' : 'bg-muted'
              }`}>
                {tabCount}
              </span>
            </button>
          );
        })}
      </div>

      {/* Recherche */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par N°, nom ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="requests-search-input"
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[170px]" data-testid="status-filter-select">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="approved">Approuvé</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full md:w-[160px] justify-between">
                  <span>{DATE_FILTERS.find(d => d.value === dateFilter)?.label || 'Date'}</span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-4">
                <div className="space-y-3">
                  {DATE_FILTERS.map(option => (
                    <div 
                      key={option.value}
                      className={`px-3 py-2 rounded-md cursor-pointer transition-colors ${
                        dateFilter === option.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                      onClick={() => {
                        setDateFilter(option.value);
                        if (option.value !== 'custom') {
                          setCustomDateStart('');
                          setCustomDateEnd('');
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                  {dateFilter === 'custom' && (
                    <div className="space-y-2 pt-2 border-t">
                      <Input
                        type="date"
                        value={customDateStart}
                        onChange={(e) => setCustomDateStart(e.target.value)}
                        placeholder="Date début"
                      />
                      <Input
                        type="date"
                        value={customDateEnd}
                        onChange={(e) => setCustomDateEnd(e.target.value)}
                        placeholder="Date fin"
                      />
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Reset */}
            {hasActiveFilters && (
              <Button variant="ghost" size="icon" onClick={resetFilters}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contenu */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : viewMode === 'table' ? (
        /* Vue Tableau */
        <Card>
          <CardContent className="p-0">
            {filteredRequests.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {hasActiveFilters ? 'Aucune demande ne correspond aux filtres' : 'Aucune demande'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">N° Demande</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type / Source</TableHead>
                    <TableHead>Validation</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => {
                    const typeConfig = REQUEST_TYPES[request.request_type] || {};
                    const statusConfig = STATUS_CONFIG[request.status] || { label: request.status, color: 'bg-gray-100' };
                    const canApprove = ['pending', 'advice_requested'].includes(request.status);
                    const isVideo = request.request_type === 'appointment' && (request.meeting_type === 'online' || request.meeting_type === 'video');
                    const isAuto = request.validation_mode === 'auto';
                    
                    return (
                      <TableRow key={`${request.request_type}-${request.id}`} data-testid={`request-row-${request.id}`}>
                        <TableCell>
                          <button
                            onClick={() => openDetail(request)}
                            className="font-mono text-sm bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors cursor-pointer flex items-center gap-1.5"
                          >
                            {request.request_number || `#${request.id?.substring(0, 8)}`}
                            {hasCustomAnswers(request) && (
                              <MessageSquare className="w-3 h-3 text-blue-500" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2">
                            <div>
                              <div className="font-medium flex items-center gap-1.5">
                                <span>{request.client_name}</span>
                                {request.is_new_client && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] uppercase px-1.5 py-0" data-testid={`new-client-badge-${request.id}`}>
                                    <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                                    Nouveau
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{request.client_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1 flex-wrap">
                              <Badge className={typeConfig.color}>
                                {request.request_type === 'appointment'
                                  ? (isVideo ? 'RDV Visio' : 'RDV Physique')
                                  : typeConfig.label}
                              </Badge>
                              {isVideo && (
                                request.meeting_link
                                  ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Lien OK</Badge>
                                  : <Badge className="bg-red-100 text-red-700 border-red-200">Lien manquant</Badge>
                              )}
                            </div>
                            {request.widget_name && (
                              <span className="text-xs text-muted-foreground italic">
                                via {request.widget_name}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isAuto ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1" data-testid={`validation-auto-${request.id}`}>
                              <Zap className="w-3 h-3" />
                              Auto
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-50 text-orange-700 border border-orange-200 gap-1" data-testid={`validation-manual-${request.id}`}>
                              <ShieldCheck className="w-3 h-3" />
                              Manuelle
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(request.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => openDetail(request)}
                              title="Voir les détails"
                              data-testid={`view-request-${request.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canApprove && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                                  onClick={() => handleApprove(request)}
                                  title="Approuver"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                  onClick={() => handleReject(request)}
                                  title="Refuser"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {request.is_archived ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-100"
                                onClick={() => handleArchive(request, false)}
                                title="Désarchiver"
                                data-testid={`unarchive-${request.id}`}
                              >
                                <ArchiveRestore className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-100"
                                onClick={() => handleArchive(request, true)}
                                title="Archiver"
                                data-testid={`archive-${request.id}`}
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Vue Kanban */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {KANBAN_COLUMNS.map(column => (
            <div key={column.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{column.title}</h3>
                <Badge variant="secondary">{kanbanData[column.id]?.length || 0}</Badge>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
                {kanbanData[column.id]?.map(request => (
                  <RequestCard key={`${request.request_type}-${request.id}`} request={request} />
                ))}
                {kanbanData[column.id]?.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune demande</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de détails */}
      <RequestDetailModal
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        request={selectedRequest}
        type={selectedRequest?.request_type || 'subscription'}
        onRequestUpdated={fetchAllRequests}
      />
    </div>
  );
}
